import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * 모바일(xs) 상단 앱바와 각 페이지 헤더(TitleBox, 영업기회 상세 등)를 하나로 합치기 위한
 * 등록 채널. 페이지가 title/뒤로가기/캡션(경로 또는 "번호 · 단계")을 등록하면
 * DrawerTemplate 의 AppBar 가 그 값으로 축약 1~2줄을 그린다.
 *
 * 아무 페이지도 등록하지 않으면(예: 대시보드) 기존 앱바(☰ + 아이콘들)가 그대로 유지된다.
 */
const MobilePageHeaderContext = createContext(null);

export const MobilePageHeaderProvider = ({ children }) => {
  const [headerConfig, setHeaderConfig] = useState(null);
  // DrawerTemplate 의 스크롤 컨테이너(overflowY:auto인 Box)를 가리킨다.
  // caption(경로/단계) 영역을 탭했을 때 맨 위로 스크롤하는 데 쓴다.
  const scrollTargetRef = useRef(null);

  const registerHeader = useCallback((config) => {
    setHeaderConfig(config);
    return () => {
      setHeaderConfig((current) => (current === config ? null : current));
    };
  }, []);

  const scrollContentToTop = useCallback(() => {
    const el = scrollTargetRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const value = useMemo(
    () => ({ headerConfig, registerHeader, scrollTargetRef, scrollContentToTop }),
    [headerConfig, registerHeader, scrollContentToTop],
  );

  return (
    <MobilePageHeaderContext.Provider value={value}>
      {children}
    </MobilePageHeaderContext.Provider>
  );
};

/**
 * 페이지가 모바일 앱바에 자신의 title/onBack/caption/overflow 를 등록한다.
 * config 가 falsy(null/undefined) 면 등록하지 않는다 — 그 화면은 기본 앱바를 그대로 쓴다
 * (예: title 이 아직 로딩 전이거나, 대시보드처럼 애초에 등록할 제목이 없는 화면).
 *
 * - title: 문자열. 말줄임으로 한 줄.
 * - onBack: 함수. 있으면 ← 뒤로가기, 없으면 ☰ 메뉴 열기.
 * - caption: ReactNode. 제목 아래 한 줄(경로 또는 "번호 · 단계"). 스크롤 다운 시 숨김.
 * - overflow: ReactNode. 있으면 "⋯" 버튼이 나타나고 눌렀을 때 메뉴 안에 렌더된다
 *   (예: 상세 화면의 수정/삭제 버튼).
 *
 * 매 렌더 이후 최신 값으로 재등록한다 — title/caption 이 비동기로 채워지는 화면(상세 조회 등)도
 * 정확히 반영하기 위해서다. 언마운트되거나 다음 페이지가 등록하면 자동으로 정리된다.
 */
// 등록된 값 중 함수/노드는 매 렌더 새로 만들어지므로, 그 자체를 등록하면 Provider 상태가 매번 바뀌어
// 페이지가 다시 그려지고 → 또 등록되는 무한 렌더가 생긴다(2026-10-28 운영 멈춤 원인).
// 그래서 "모양이 바뀌었을 때(sig)"만 등록하고, 함수·노드는 ref로 항상 최신 값을 읽게 한다.
const LatestNode = ({ refObj, field }) => (refObj.current ? refObj.current[field] || null : null);

export const useMobilePageHeader = (config) => {
  const ctx = useContext(MobilePageHeaderContext);
  const latestRef = useRef(config);
  latestRef.current = config;

  const sig = config
    ? [config.title ?? '', config.captionKey ?? '', Boolean(config.onBack), Boolean(config.caption), Boolean(config.overflow)].join('|')
    : '';

  const registerHeader = ctx ? ctx.registerHeader : null; // useCallback으로 고정된 함수 — ctx 객체 자체는 매번 바뀌므로 의존하면 안 됨
  React.useEffect(() => {
    const current = latestRef.current;
    if (!registerHeader || !current) return undefined;
    return registerHeader({
      title: current.title,
      onBack: current.onBack ? (...args) => latestRef.current?.onBack?.(...args) : undefined,
      caption: current.caption ? <LatestNode refObj={latestRef} field="caption" /> : null,
      overflow: current.overflow ? <LatestNode refObj={latestRef} field="overflow" /> : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerHeader, sig]);
};

export const useMobilePageHeaderContext = () => useContext(MobilePageHeaderContext);
